import { FC, useState, useEffect, useRef } from 'react';
import { motion, Variants } from 'framer-motion';
import { Action } from '../../types/action';
import ActionExecutionModal from './ActionExecutionModal';
import { FiCheckCircle, FiAlertCircle, FiClock, FiArrowRight, FiStar, FiTrendingUp, FiActivity } from 'react-icons/fi';
import { UserRole } from './ActionsPanel';
import { trackRecommendationShown, trackRecommendationClicked, trackRecommendationAccepted } from '../../services/recommendationEventService';
import OutcomeBadge from './OutcomeBadge';
import type { RecommendationOutcome } from '../../services/recommendationOutcomesService';

interface ActionCardProps {
  action: Action;
  onComplete: (actionId: string) => void;
  onTogglePin?: (actionId: string) => void;
  isPinned?: boolean;
  shouldAnimate?: boolean;
  role: UserRole;
  /** P4A Phase 6: Outcome data for this recommendation (if available) */
  outcome?: RecommendationOutcome;
}

const ActionCard: FC<ActionCardProps> = ({
  action,
  onComplete,
  onTogglePin,
  isPinned,
  shouldAnimate = true,
  role,
  outcome
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const hasTrackedShown = useRef(false);

  // P3: Track behavioral recommendation shown event (once per mount)
  const isBehavioral = action.id.startsWith('behavioral-');
  useEffect(() => {
    if (isBehavioral && !hasTrackedShown.current) {
      hasTrackedShown.current = true;
      trackRecommendationShown(action.id, { role });
    }
  }, [action.id, isBehavioral, role]);

  // Reset animation state when action changes
  useEffect(() => {
    setHasAnimated(false);
  }, [action.id]);
  
  // Get role-specific colors
  const getRoleColors = () => {
    switch (role) {
      case 'CEO':
        return {
          primary: 'bg-brand-purple hover:bg-brand-purple/80',
          ring: 'ring-brand-purple/70',
          shadow: 'shadow-[0_0_10px_rgba(138,43,226,0.5)]',
          accent: 'bg-brand-purple'
        };
      case 'VP of Lending':
        return {
          primary: 'bg-brand-link hover:bg-brand-link/80',
          ring: 'ring-brand-link/70',
          shadow: 'shadow-[0_0_10px_rgba(59,130,246,0.5)]',
          accent: 'bg-brand-link'
        };
      case 'Loan Advisor':
        return {
          primary: 'bg-brand-blue hover:bg-brand-blue/80',
          ring: 'ring-brand-blue/70',
          shadow: 'shadow-[0_0_10px_rgba(99,102,241,0.5)]',
          accent: 'bg-brand-blue'
        };
      default:
        return {
          primary: 'bg-brand-purple hover:bg-brand-purple/80',
          ring: 'ring-brand-purple/70',
          shadow: 'shadow-[0_0_10px_rgba(138,43,226,0.5)]',
          accent: 'bg-brand-purple'
        };
    }
  };
  
  const roleColors = getRoleColors();
  
  // Animation variants
  const cardAnimationVariants: Variants = {
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        opacity: { duration: 0.4, ease: "easeOut" as const },
        y: { duration: 0.4, ease: "easeOut" as const },
        scale: { duration: 0.4, ease: [0.175, 0.885, 0.32, 1.275] as [number, number, number, number] }
      }
    },
    initial: {
      opacity: 0,
      y: 15,
      scale: 0.98
    },
    noAnimation: {
      opacity: 1,
      y: 0,
      scale: 1
    }
  };
  
  // Only animate if shouldAnimate is true AND this card hasn't animated yet
  const finalShouldAnimate = shouldAnimate && !hasAnimated;
  
  // When animation completes, mark this card as having animated
  const handleAnimationComplete = () => {
    if (finalShouldAnimate) {
      setHasAnimated(true);
    }
  };
  
  const getPriorityColors = (priority: string) => {
    switch (priority) {
      case 'critical':
        return {
          bg: 'bg-rose-600/25',
          text: 'text-rose-500',
          icon: <FiAlertCircle size={16} className="text-rose-500" />
        };
      case 'high':
        return {
          bg: 'bg-red-500/20',
          text: 'text-red-400',
          icon: <FiAlertCircle size={16} className="text-red-400" />
        };
      case 'medium':
        return {
          bg: 'bg-amber-500/20',
          text: 'text-amber-400',
          icon: <FiClock size={16} className="text-amber-400" />
        };
      default:
        return {
          bg: 'bg-blue-500/20',
          text: 'text-blue-400',
          icon: <FiCheckCircle size={16} className="text-blue-400" />
        };
    }
  };
  
  const priorityStyle = getPriorityColors(action.priority);
  
  const getImpactColor = (impact: number) => {
    if (impact > 75) return 'text-green-400';
    if (impact > 40) return 'text-amber-400';
    return 'text-slate-500';
  };

  // Check if we should show member photo (only for Loan Advisor actions with member data)
  const showMemberPhoto = action.memberId && action.memberName && action.applicableRoles.includes('Loan Advisor');

  // Check if this action should have a very light grey background
  const specialGreyActions = [
    'Enable Personalized Financial Insights',
    'Finalize Local Business Partnership',
    'Counter Competitor Rate Campaign',
    'Evaluate Credit Union Acquisition',
    'Optimize Financial Health Across Portfolio'
  ];
  const hasGreyBackground = specialGreyActions.includes(action.title);

  return (
    <>
      <motion.div
        variants={cardAnimationVariants}
        initial={finalShouldAnimate ? "initial" : "noAnimation"}
        animate="animate"
        onAnimationComplete={handleAnimationComplete}
      >
        <div className={`${hasGreyBackground ? 'bg-slate-50' : 'bg-white'} rounded-lg shadow-sm flex flex-col h-full ${
            isPinned 
              ? `ring-2 ${roleColors.ring}` 
              : ''
          }`}>
          {/* Card Header */}
          <div className={`flex items-start justify-between p-4 border-b ${
            isPinned ? 'border-slate-300' : 'border-slate-200'
          } relative`}>
            {/* Pin Button */}
            {onTogglePin && (
              <button
                onClick={() => onTogglePin(action.id)}
                className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-all transform hover:scale-110 ${
                  isPinned 
                    ? roleColors.accent + ' text-white' 
                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600'
                }`}
                title={isPinned ? "Unpin this recommendation" : "Pin this recommendation"}
              >
                <FiStar size={16} className={isPinned ? "fill-current" : ""} />
              </button>
            )}
            
            <div className="flex-grow flex items-center gap-4 max-w-[65%]">
              {/* Member Photo (if applicable) */}
              {showMemberPhoto && (
                <div className="flex-shrink-0">
                  {action.memberPhoto ? (
                    <div className="relative">
                      <img 
                        src={action.memberPhoto} 
                        alt={action.memberName}
                        className={`w-16 h-16 rounded-full object-cover border-2 ${
                          isPinned 
                            ? `border-${role === 'CEO' ? 'brand-purple' : role === 'VP of Lending' ? 'brand-link' : 'brand-blue'} ${roleColors.shadow}` 
                            : 'border-slate-300'
                        }`}
                      />
                      {isPinned && (
                        <span className={`absolute bottom-0 right-0 ${roleColors.accent} text-white rounded-full p-1`}>
                          <FiStar size={12} className="fill-current" />
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className={`relative w-16 h-16 rounded-full flex items-center justify-center text-slate-800 text-lg font-medium ${
                      isPinned 
                        ? `bg-slate-200 border-2 border-${role === 'CEO' ? 'brand-purple' : role === 'VP of Lending' ? 'brand-link' : 'brand-blue'} ${roleColors.shadow}` 
                        : 'bg-slate-100'
                    }`}>
                      {action.memberName?.split(' ').map(name => name[0]).join('')}
                      {isPinned && (
                        <span className={`absolute bottom-0 right-0 ${roleColors.accent} text-white rounded-full p-1`}>
                          <FiStar size={12} className="fill-current" />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex flex-col justify-center">
                <div className="flex items-center flex-wrap space-x-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    isPinned 
                      ? priorityStyle.bg.replace('/20', '/30') 
                      : priorityStyle.bg
                  } ${priorityStyle.text}`}>
                    <span className="flex items-center">
                      {priorityStyle.icon}
                      <span className="ml-1">{action.priority} priority</span>
                    </span>
                  </span>
                  <span className={`text-xs ${isPinned ? 'text-slate-500' : 'text-slate-400'}`}>
                    {action.category}
                  </span>
                  {/* P2C: Badge for data-driven behavioral recommendations */}
                  {action.id.startsWith('behavioral-') && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                      Live Insight
                    </span>
                  )}
                </div>
                <h3 className="mt-1.5 text-lg font-medium text-slate-800 leading-tight">{action.title}</h3>
              </div>
            </div>
            
            {/* Display the impact value */}
            <div className="text-right mr-8 flex-shrink-0 pl-2">
              <div className={`text-xl font-bold ${getImpactColor(action.impact.value)}`}>
                {action.impact.type === 'financial' ? '$' : ''}
                {action.impact.value.toLocaleString()}
                {action.impact.type === 'percentage' ? '%' : ''}
              </div>
              <div className="text-xs text-slate-400">{action.impact.description}</div>
            </div>
          </div>
          
          {/* Card Body */}
          <div className={`p-4 flex-grow ${isPinned ? 'text-slate-800' : 'text-slate-600'}`}>
            <p className="text-sm">{action.description}</p>
            
            {/* P3.3: Explainability Section — trigger reason + performance label */}
            {isBehavioral && (action.triggerReason || action.performanceLabel) && (
              <div className="mt-4 space-y-2">
                {action.triggerReason && (
                  <div className="flex items-start gap-2">
                    <FiActivity size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className={`text-xs font-medium ${isPinned ? 'text-slate-600' : 'text-slate-400'} uppercase tracking-wider`}>Trigger</span>
                      <p className="text-xs text-slate-500">{action.triggerReason}</p>
                    </div>
                  </div>
                )}
                {action.performanceLabel && (
                  <div className="flex items-start gap-2">
                    <FiTrendingUp size={14} className={`mt-0.5 flex-shrink-0 ${
                      action.performanceLabel.startsWith('High') ? 'text-green-500' :
                      action.performanceLabel.startsWith('Moderate') ? 'text-amber-500' :
                      action.performanceLabel.startsWith('Low') ? 'text-red-400' :
                      'text-slate-400'
                    }`} />
                    <div>
                      <span className={`text-xs font-medium ${isPinned ? 'text-slate-600' : 'text-slate-400'} uppercase tracking-wider`}>Performance</span>
                      <p className={`text-xs ${
                        action.performanceLabel.startsWith('High') ? 'text-green-600' :
                        action.performanceLabel.startsWith('Moderate') ? 'text-amber-600' :
                        action.performanceLabel.startsWith('Low') ? 'text-red-500' :
                        'text-slate-500'
                      }`}>{action.performanceLabel}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* P4A Phase 6: OutcomeBadge — behavioral recs only, aggregate data, non-causal */}
            {isBehavioral && outcome && (
              <OutcomeBadge outcome={outcome} />
            )}

            {/* Data Sources Section */}
            {action.dataSources && action.dataSources.length > 0 && (
              <div className="mt-4">
                <h4 className={`text-xs font-medium ${isPinned ? 'text-slate-600' : 'text-slate-400'} uppercase tracking-wider mb-2`}>Based on</h4>
                <div className="flex flex-wrap gap-2">
                  {action.dataSources.map((source, index) => (
                    <span key={index} className={`${
                      isPinned
                        ? 'bg-slate-200 text-slate-800'
                        : 'bg-slate-100 text-slate-500'
                    } px-2 py-1 rounded text-xs`}>
                      {source}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Card Footer */}
          <div className={`p-4 ${isPinned ? 'border-t border-slate-300' : 'border-t border-slate-200'}`}>
            <div className="flex justify-between items-center">
              <button 
                className={`text-xs ${isPinned ? 'text-slate-600 hover:text-slate-800' : 'text-slate-400 hover:text-slate-600'} transition-colors`}
                onClick={() => { if (import.meta.env.DEV) console.log('View insights for:', action.id); }}
              >
                View insights
              </button>
              
              <button
                className={`flex items-center text-white ${roleColors.primary} px-4 py-2 rounded-md transition-colors`}
                onClick={() => {
                  if (isBehavioral) trackRecommendationClicked(action.id, { role });
                  setIsModalOpen(true);
                }}
              >
                Take action
                <FiArrowRight className="ml-2" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Action Execution Modal */}
      <ActionExecutionModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        action={action}
        role={role}
        onComplete={() => {
          // P4A: Fire accepted event (not dismissed) when lender completes the action workflow.
          // This triggers baseline snapshot creation for outcome tracking.
          // Guardrail: only fire if segmentKey is present (P4A.5 requirement).
          if (isBehavioral) {
            if (action.segmentKey) {
              trackRecommendationAccepted(action.id, action.segmentKey, { role });
            } else if (import.meta.env.DEV) {
              console.warn(`[P4A] Missing segmentKey for ${action.id} — accepted event not fired. Outcome tracking will not create a baseline.`);
            }
          }
          onComplete(action.id);
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

export default ActionCard; 